import React, { useState } from 'react';
import CourseValidator from '../utils/courseValidator';
import '../styles/CourseValidator.css';

const CourseValidatorComponent = () => {
    const [isValidating, setIsValidating] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState('soccer-card-making');

    const availableCourses = [
        'soccer-card-making',
        // 추후 다른 코스들 추가
    ];

    const runValidation = async () => {
        setIsValidating(true);
        setValidationResult(null);

        try {
            const validator = new CourseValidator(selectedCourse);
            const result = await validator.validate();
            setValidationResult(result);
        } catch (error) {
            setValidationResult({
                courseId: selectedCourse,
                isValid: false,
                summary: { errors: 1, warnings: 0, info: 0, totalIssues: 1 },
                details: {
                    errors: [{ code: 'VALIDATION_FAILED', message: `검증 실행 오류: ${error.message}`, level: 'error' }],
                    warnings: [],
                    info: []
                }
            });
        } finally {
            setIsValidating(false);
        }
    };

    const getStatusIcon = (isValid, hasWarnings) => {
        if (!isValid) return '❌';
        if (hasWarnings) return '⚠️';
        return '✅';
    };

    const getStatusText = (isValid, hasWarnings) => {
        if (!isValid) return '실패';
        if (hasWarnings) return '경고 있음';
        return '통과';
    };

    const getStatusClass = (isValid, hasWarnings) => {
        if (!isValid) return 'status-error';
        if (hasWarnings) return 'status-warning';
        return 'status-success';
    };

    return (
        <div className="course-validator">
            <div className="validator-header">
                <h1>📋 코스 검증기</h1>
                <p>코스 구조와 완성도를 자동으로 검증합니다.</p>
            </div>

            <div className="validator-controls">
                <div className="course-selector">
                    <label htmlFor="course-select">검증할 코스:</label>
                    <select
                        id="course-select"
                        value={selectedCourse}
                        onChange={(e) => setSelectedCourse(e.target.value)}
                        disabled={isValidating}
                    >
                        {availableCourses.map(course => (
                            <option key={course} value={course}>
                                {course}
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    className="validate-button"
                    onClick={runValidation}
                    disabled={isValidating}
                >
                    {isValidating ? '🔍 검증 중...' : '🚀 검증 시작'}
                </button>
            </div>

            {validationResult && (
                <div className="validation-results">
                    <div className="results-header">
                        <h2>검증 결과</h2>
                        <div className={`validation-status ${getStatusClass(
                            validationResult.isValid, 
                            validationResult.summary.warnings > 0
                        )}`}>
                            <span className="status-icon">
                                {getStatusIcon(validationResult.isValid, validationResult.summary.warnings > 0)}
                            </span>
                            <span className="status-text">
                                {getStatusText(validationResult.isValid, validationResult.summary.warnings > 0)}
                            </span>
                        </div>
                    </div>

                    <div className="results-summary">
                        <div className="summary-card">
                            <h3>📊 요약</h3>
                            <div className="summary-stats">
                                <div className="stat-item error">
                                    <span className="stat-number">{validationResult.summary.errors}</span>
                                    <span className="stat-label">오류</span>
                                </div>
                                <div className="stat-item warning">
                                    <span className="stat-number">{validationResult.summary.warnings}</span>
                                    <span className="stat-label">경고</span>
                                </div>
                                <div className="stat-item info">
                                    <span className="stat-number">{validationResult.summary.info}</span>
                                    <span className="stat-label">정보</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="results-details">
                        {/* 오류 */}
                        {validationResult.details.errors.length > 0 && (
                            <div className="detail-section errors">
                                <h3>❌ 오류 ({validationResult.details.errors.length}개)</h3>
                                <div className="issue-list">
                                    {validationResult.details.errors.map((error, index) => (
                                        <div key={index} className="issue-item error">
                                            <div className="issue-code">[{error.code}]</div>
                                            <div className="issue-message">{error.message}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 경고 */}
                        {validationResult.details.warnings.length > 0 && (
                            <div className="detail-section warnings">
                                <h3>⚠️ 경고 ({validationResult.details.warnings.length}개)</h3>
                                <div className="issue-list">
                                    {validationResult.details.warnings.map((warning, index) => (
                                        <div key={index} className="issue-item warning">
                                            <div className="issue-code">[{warning.code}]</div>
                                            <div className="issue-message">{warning.message}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 정보 */}
                        {validationResult.details.info.length > 0 && (
                            <div className="detail-section info">
                                <h3>ℹ️ 정보 ({validationResult.details.info.length}개)</h3>
                                <div className="issue-list">
                                    {validationResult.details.info.map((info, index) => (
                                        <div key={index} className="issue-item info">
                                            <div className="issue-code">[{info.code}]</div>
                                            <div className="issue-message">{info.message}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {validationResult.isValid && validationResult.summary.warnings === 0 && (
                        <div className="success-message">
                            <h3>🎉 완벽합니다!</h3>
                            <p>코스가 모든 요구사항을 충족하며 배포 준비가 완료되었습니다.</p>
                        </div>
                    )}
                </div>
            )}

            <div className="validator-info">
                <h3>🔍 검증 항목</h3>
                <div className="info-grid">
                    <div className="info-item">
                        <h4>📁 파일 구조</h4>
                        <ul>
                            <li>meta.json 존재 여부</li>
                            <li>스텝 파일들 존재 여부</li>
                            <li>언어별 article 파일</li>
                        </ul>
                    </div>
                    <div className="info-item">
                        <h4>📝 컨텐츠 구조</h4>
                        <ul>
                            <li>필수 섹션 존재 여부</li>
                            <li>체크포인트 구조</li>
                            <li>피드백 메시지</li>
                        </ul>
                    </div>
                    <div className="info-item">
                        <h4>💻 코드 구조</h4>
                        <ul>
                            <li>초기 코드 제공</li>
                            <li>정답 코드 제공</li>
                            <li>검증 로직 구조</li>
                        </ul>
                    </div>
                    <div className="info-item">
                        <h4>🌐 다국어 지원</h4>
                        <ul>
                            <li>언어별 컨텐츠</li>
                            <li>번역 완성도</li>
                            <li>일관성 체크</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseValidatorComponent;